import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const StainlessSteelHopperPage = () => {
  const projectImages = [
    {
      src: "https://customer-assets.emergentagent.com/job_ec005545-4634-414e-ac21-6d3a92f4c2b3/artifacts/mveg10tz_gsm%20%20%20hopper%20005.jpg",
      alt: "Stainless Steel Hopper - Side View"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_ec005545-4634-414e-ac21-6d3a92f4c2b3/artifacts/z65i0t54_gsm%20%20%20hopper%20002.jpg",
      alt: "Stainless Steel Hopper - Detail View"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_ec005545-4634-414e-ac21-6d3a92f4c2b3/artifacts/6rygnd9t_gsm%20%20%20hopper%20003.JPG",
      alt: "Stainless Steel Hopper - Assembly View"
    }
  ];

  const projectFeatures = [
    "Food-grade 304/316 stainless steel",
    "Custom capacity configurations",
    "Conical discharge design",
    "Polished interior finish",
    "Integrated support frame",
    "Easy-clean sanitary design",
    "Heavy-duty construction",
    "Full welded seams"
  ];

  return (
    <div data-testid="stainless-steel-hopper-page" className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://customer-assets.emergentagent.com/job_ec005545-4634-414e-ac21-6d3a92f4c2b3/artifacts/mveg10tz_gsm%20%20%20hopper%20005.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 to-slate-900" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/projects" 
            className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Projects
          </Link>
          <div className="text-center">
            <p className="text-blue-400 uppercase tracking-widest text-sm mb-4">
              Project Showcase
            </p>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase tracking-tight mb-6"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Stainless Steel <span className="text-blue-500">Hopper</span>
            </h1>
            <p className="text-slate-300 text-lg max-w-3xl mx-auto">
              Custom fabricated stainless steel hoppers for manufacturing and processing 
              applications, featuring conical discharge design and sanitary construction.
            </p>
          </div>
        </div>
      </section>

      {/* Project Details */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Main Image */}
            <div>
              <img
                src={projectImages[0].src}
                alt={projectImages[0].alt}
                className="w-full h-[500px] object-cover rounded-sm"
              />
            </div>

            {/* Project Info */}
            <div>
              <h2 
                className="text-3xl font-bold text-white uppercase tracking-tight mb-6"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Project <span className="text-blue-500">Overview</span>
              </h2>
              <p className="text-slate-400 mb-6 leading-relaxed">
                These stainless steel hoppers were custom designed and fabricated for a 
                manufacturing facility. The project includes multiple hopper units with 
                conical discharge bottoms for efficient material flow and complete drainage.
              </p>
              <p className="text-slate-400 mb-8 leading-relaxed">
                Constructed from food-grade stainless steel with fully welded seams and 
                polished interior surfaces, these hoppers meet stringent hygiene requirements 
                for food processing and pharmaceutical manufacturing applications.
              </p>

              <h3 
                className="text-xl font-bold text-white uppercase tracking-tight mb-4"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Key Features
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {projectFeatures.map((feature, idx) => (
                  <div key={idx} className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span className="text-slate-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button 
                    data-testid="project-enquiry-btn"
                    className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm uppercase tracking-wider w-full sm:w-auto"
                  >
                    Enquire About Similar Project
                  </Button>
                </Link>
                <Link to="/services">
                  <Button 
                    variant="outline"
                    className="border-slate-700 hover:border-blue-500 hover:text-blue-400 rounded-sm uppercase tracking-wider w-full sm:w-auto"
                  >
                    View Our Services
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-blue-400 uppercase tracking-widest text-sm mb-4">
              Project Gallery
            </p>
            <h2 
              className="text-3xl font-bold text-white uppercase tracking-tight"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              More <span className="text-blue-500">Images</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {projectImages.map((image, idx) => (
              <div 
                key={idx}
                className="relative group overflow-hidden rounded-sm"
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-slate-950/0 group-hover:bg-slate-950/50 transition-colors duration-300" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight mb-6"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Need Custom Hopper Fabrication?
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            Our team specializes in designing and fabricating custom stainless steel hoppers 
            for manufacturing, food processing, and industrial applications. Contact us for a free estimate.
          </p>
          <Link to="/contact">
            <Button 
              size="lg"
              className="bg-white text-blue-600 hover:bg-slate-100 rounded-sm uppercase tracking-wider"
            >
              Get a Free Quote
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default StainlessSteelHopperPage;
